// @ai-generated 2026-08-24T12:24:35+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { type ApiContainerAction } from '@Model';
import { type AsyncStateUpdate, useTranslate } from '@synqo/frontend-component-library';
import { billsAsAnotherType, hasRemarks } from './actionDetails';
import { ContainerBilledAsCell } from './ContainerBilledAsCell';
import { ContainerLineConfirmation } from './ContainerLineConfirmation';
import { ContainerActionLine, ContainerLineFields } from './ContainerLineLayout';
import { ContainerMaterialCell } from './ContainerMaterialCell';
import { canPickNumber, type ContainerOrderState, isContentWeighed, plannedNumberOf } from './containerRowState';
import { ContainerNumberCell } from './ContainerNumberCell';
import { ContainerRemarksCell } from './ContainerRemarksCell';
import { ContainerTypeCell } from './ContainerTypeCell';
import { fieldToOpen, lineState, whyLineCannotBeConfirmed } from './lineConfirmation';
import { type ContainerMaterial } from './useContainerMaterial';

interface IndividualContainerRowProps {
    action: ApiContainerAction;
    /** Path to the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** Whether the type and the material may be linked. */
    canSetLinks: boolean;
    canDelete: boolean;
    /** Whether the user may confirm lines and take confirmations back. */
    canMarkForBooking: boolean;
    isMarking: boolean;
    completing: boolean;
    focused?: boolean;
    material: ContainerMaterial;
    orderState: ContainerOrderState;
    requestUpdateState: AsyncStateUpdate<ApiContainerAction>;
    onDelete: (actionIds: string[]) => void;
    onMarkForBooking: (actionId: string) => void;
    onUnmarkForBooking: (actionId: string) => void;
}

/**
 * One container of an individually managed type as one line, carrying the number it is booked under.
 *
 * A confirmed line is locked until its confirmation is taken back. A booked line stays open, so a
 * completed order can be corrected.
 */
export function IndividualContainerRow({
    action,
    baseApiObjectPath,
    canEdit,
    canSetLinks,
    canDelete,
    canMarkForBooking,
    isMarking,
    completing,
    focused = false,
    material,
    orderState,
    requestUpdateState,
    onDelete,
    onMarkForBooking,
    onUnmarkForBooking,
}: IndividualContainerRowProps) {
    const i18n = useTranslate('');

    const state = lineState(action.booking_status);
    const isClosed = state === 'confirmed';
    const blockedBy = whyLineCannotBeConfirmed([action], (row) => Boolean(row.container_type.foreign_key));
    const openField = focused ? fieldToOpen(action, Boolean(action.container_type.foreign_key), completing) : undefined;

    const type = (
        <ContainerTypeCell
            key={openField === 'type' ? 'open' : 'closed'}
            autoOpen={openField === 'type'}
            action={action}
            baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
            canEdit={canSetLinks && !isClosed}
            correcting={orderState.correcting}
            requestUpdateState={requestUpdateState}
        />
    );

    const details = (
        <>
            <ContainerRemarksCell
                action={action}
                canEdit={canSetLinks && !isClosed}
                requestUpdateState={requestUpdateState}
            />
            <ContainerBilledAsCell
                action={action}
                baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                canEdit={canSetLinks && !isClosed}
                requestUpdateState={requestUpdateState}
            />
        </>
    );

    const status = (
        <ContainerLineConfirmation
            action={action.action}
            state={state}
            completing={completing}
            canConfirm={canMarkForBooking}
            locked={isMarking}
            blockedBy={blockedBy && i18n(blockedBy)}
            onConfirm={() => onMarkForBooking(action.id)}
            onUnconfirm={() => onUnmarkForBooking(action.id)}
        />
    );

    return (
        <ContainerActionLine
            action={action.action}
            removeLabel={i18n('Container entfernen')}
            invalid={orderState.foreignToSite.has(action.id) ? i18n('Steht nicht am gewählten Standort.') : undefined}
            onRemove={canDelete && !isClosed ? () => onDelete([action.id]) : undefined}
            details={details}
            detailsLabel="Bemerkung und Abrechnung"
            hasDetails={hasRemarks(action) || billsAsAnotherType(action)}
        >
            <ContainerLineFields
                type={type}
                material={
                    <ContainerMaterialCell
                        key={openField === 'content' ? 'open' : 'closed'}
                        autoOpen={openField === 'content'}
                        actions={[action]}
                        baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                        canEdit={canSetLinks && !isClosed && !isContentWeighed(action, orderState.positions)}
                        correcting={orderState.correcting}
                        material={material}
                        requestUpdateAction={(updater) => requestUpdateState(updater)}
                    />
                }
                detail={
                    action.container_type.foreign_key ? (
                        <ContainerNumberCell
                            key={openField === 'number' ? 'open' : 'closed'}
                            autoOpen={openField === 'number'}
                            completing={completing}
                            action={action}
                            baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                            canEdit={
                                canEdit &&
                                !isClosed &&
                                canPickNumber(action, orderState.completedAt, orderState.numbersAtEditStart)
                            }
                            plannedNumber={plannedNumberOf(
                                action,
                                orderState.numbersAtEditStart,
                                orderState.correcting,
                            )}
                            requestUpdateState={requestUpdateState}
                        />
                    ) : undefined
                }
                status={status}
            />
        </ContainerActionLine>
    );
}
